<script setup lang="ts">
import type { ChangedKeysToFilterSearch, FiltersBarProps } from '.';
import type {
	SearchParams,
	SuggestionCondoResult
} from '~/components/SearchSuggest';
import type { AllowedValues } from '~/composables/useListing/filter';
import type {
	SearchSelectionEvent,
	SelectAddressEvent
} from '~/composables/useNewSearchSuggest';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { FilterApplied } from '~/types/mixpanel';

import { SearchSuggestDialog } from '#components';
import { useEventDataFilters } from '~/composables/events/useEventDataFilters/useEventDataFilters';
import { useEvents } from '~/composables/events/useEvents';
import { useTrackSearchPropertyEvents } from '~/composables/events/useTrackSearchPropertyEvents';
import { useAddresses } from '~/composables/useAddressesWrapper';
import { searchSuggestBus } from '~/composables/useNewSearchSuggest';
import { usePropertiesListingHeader } from '~/composables/usePropertiesListingHeader';
import { PropertyPublicationStatus } from '~/core/entities/api/ApiProperty';
import { MixpanelProps } from '~/core/service/mixpanel';
import { PropertyFiltersPresenter } from '~/presenters/PropertyFiltersPresenter';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { getAreaRangeLabel, getPriceRangeLabel, isNumber } from '~~/utils';

import FilterButton from '~/components/Filters/FilterButton/Button.vue';
import FilterDropdown from '~/components/Filters/FilterDropdown/Dropdown.vue';
import SearchSuggest from '~/components/SearchSuggest/Suggest.vue';

import FilterIcon from '~icons/material-symbols-light/filter-list';

import ConvinientSelect from '../ConvinientSelect/Select.vue';
import PropertyArea from '../PropertyArea/Area.vue';
import PropertyFeatureSelect from '../PropertyFeatureSelect/FeatureSelect.vue';
import PropertyPrice from '../PropertyPrice/Price.vue';
import PropertyTypeSelect from '../PropertyTypeSelect/TypeSelect.vue';
import RegionSelect from '../RegionSelect/Select.vue';
import { navVariants } from './index';

// Pré-carrega os dados de endereços para evitar delay no primeiro clique do dropdown
// O composable usa useAsyncData com server: false, então só executa no cliente
useAddresses();

const router = useRouter();
const { searchPropertyEvents } = useEvents();

const { getFilterAppliedValues, getResumedFilterAppliedValues } =
	useEventDataFilters();

const props = withDefaults(defineProps<FiltersBarProps>(), {
	fixed: false,
	appliedFilterKeys: () => [],
	numberOfAppliedClosedFilters: 0
});

const isSidebarActive = defineModel<boolean>('isSidebarActive', {
	default: false
});

const { transform } = useHeaderAnimation({
	headerHeightInPx: 80,
	variants: ['primary', 'primary'],
	shouldOnlyResetOnTop: false
});

const filtersBarTransform = computed(() => {
	return props.fixed ? 'none' : transform.value;
});

const emit = defineEmits<{
	(event: 'update:filters', payload: FiltersBarProps['filters']): void;
	(event: 'filter-applied-for-code-search', payload: FilterApplied): void;
	(event: 'filter-applied-for-condo', payload: FilterApplied): void;
}>();

const filters = defineModel<FiltersBarProps['filters']>('filters', {
	required: true
});

const changedKeysFilters = ref<Set<string>>(new Set<string>());
const closeModalFiltersCompleted = ref<boolean>(false);

const { trackFilterApplied, trackFiltersAppliedResumed } =
	useTrackSearchPropertyEvents();

async function handleEventsFilterOnCloseField() {
	if (!changedKeysFilters.value.size) return;

	const arrayChangedKey = Array.from(changedKeysFilters.value);
	const values = getFilterAppliedValues(
		filters.value as PropertyFilters & SelectAddressEvent,
		'openFilters',
		arrayChangedKey
	);
	values[MixpanelProps.TOTAL_RESULTS] = props.countProperties;
	trackFilterApplied(values);
	changedKeysFilters.value.clear();
	closeModalFiltersCompleted.value = false;
}

const waitForCloseModal = async () => {
	return await until(() => closeModalFiltersCompleted.value).toBeTruthy();
};

const waitForTotalResults = async () => {
	return await until(() => isNumber(props.countProperties)).toBeTruthy();
};

const handleFetchingChange = async (value: boolean) => {
	if (!value && changedKeysFilters.value.size) {
		await Promise.all([waitForCloseModal(), waitForTotalResults()]);
		handleEventsFilterOnCloseField();
	}
};

watch(() => props.isFetchingProperties, handleFetchingChange);

const saveClosedFilterStatus = () => {
	closeModalFiltersCompleted.value = true;
};

function getRegionsLabel() {
	if (!filters.value.regions?.length) {
		return 'Bairros';
	}

	return filters.value.regions.join(', ');
}

function getPriceLabel() {
	return getPriceRangeLabel(
		filters.value.minAskingPrice,
		filters.value.maxAskingPrice
	);
}

function getAreaLabel() {
	return getAreaRangeLabel(filters.value.minArea, filters.value.maxArea);
}

const numberOfConvinientFilters = computed<number>(() => {
	const convinientFilters = [
		filters.value.bedrooms,
		filters.value.suites,
		filters.value.bathrooms,
		filters.value.parkingSpots
	];

	return convinientFilters.filter(Boolean).length;
});

function clearRegionFilters(currentFilters: PropertyFilters) {
	currentFilters.poiLocationLat = undefined;
	currentFilters.poiLocationLng = undefined;
	currentFilters.addressSlug = undefined;
}

function updateRegions(
	event: Record<'regions' | 'cities' | 'states', string[]>
) {
	const currentFilters = {
		...filters.value,
		...event
	};

	const isDefaultState =
		!currentFilters.regions?.length &&
		!currentFilters.cities?.length &&
		!currentFilters.macroregions?.length;

	if (isDefaultState) {
		currentFilters.cities = ['sao-paulo'];
	}

	clearRegionFilters(currentFilters);

	const newPath = PropertyFiltersPresenter.getPathFromFilters(
		currentFilters,
		{
			regions: event.regions,
			cities: event.cities,
			states: event.states
		},
		{ page: props.page }
	);

	const newFilters = PropertyFiltersPresenter.getFiltersFromPath(
		newPath,
		undefined,
		props.page
	);

	if (newFilters.regions?.length) {
		changedKeysFilters.value.add('regions');
	}
	emit('update:filters', newFilters);
}

function handleSearchSuggestSelectAddress(value: SelectAddressEvent) {
	const state = value.states?.[0]?.toUpperCase() || 'SP';

	const path = PropertyFiltersPresenter.getPathFromFilters({
		propertyTypes: [],
		states: [state],
		...value
	});

	const locationFilters = PropertyFiltersPresenter.getFiltersFromPath(path);

	const currentFilters = { ...filters.value };
	currentFilters.poiLocationLat = undefined;
	currentFilters.poiLocationLng = undefined;
	currentFilters.addressSlug = undefined;

	if (value.lat && value.lng) {
		currentFilters.poiLocationLat = value.lat;
		currentFilters.poiLocationLng = value.lng;
		currentFilters.addressSlug = value.text;
	}

	const filtersUpdated: PropertyFilters = {
		...currentFilters,
		pluscodesGroup: undefined,
		macroregions: value.macroregions ?? undefined,
		regions: value.regions ?? undefined,
		cities: value.cities ?? undefined,
		states: value.states ?? [state],
		locationSlug: locationFilters?.locationSlug ?? undefined
	};

	emit('update:filters', filtersUpdated);

	handleEventWithFetchingCheck(value, filtersUpdated);
	return;
}

const currentFilterAppliedParams = ref<Partial<FilterApplied>>({});

function handleSearchTermUpdate(value: string) {
	currentFilterAppliedParams.value[MixpanelProps.TEXT_SEARCH] = value;
}

const isFetchingPropertyByCodeSearch = shallowRef(false);

async function handleSearchSuggestSelectCode(event: SearchParams) {
	const code = event.code as string;
	isFetchingPropertyByCodeSearch.value = true;

	emit('filter-applied-for-code-search', {
		...currentFilterAppliedParams.value,
		[MixpanelProps.FILTERS_APPLIED]: 'code',
		[MixpanelProps.CURRENT_PAGE]: props.page,
		[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
		[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
		[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
		[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
		[MixpanelProps.REGION_NAME]: '',
		[MixpanelProps.CITY_NAME]: '',
		commercial_id: event.code
	} as FilterApplied);

	try {
		const [property] = await nuxtPropertiesRepository.checkOne(code);

		if (!property) {
			const path = PropertyFiltersPresenter.getPathFromFilters({
				transactionType: 'sell',
				propertyTypes: [],
				states: ['SP'],
				cities: ['sao-paulo']
			});

			const locationFilters = PropertyFiltersPresenter.getFiltersFromPath(path);
			const currentFilters = { ...filters.value };

			delete currentFilters.pluscodesGroup;

			const filtersUpdated: PropertyFilters = {
				...currentFilters,
				pluscodesGroup: undefined,
				macroregions: locationFilters?.macroregions ?? undefined,
				regions: locationFilters?.regions ?? undefined,
				cities: locationFilters?.cities ?? undefined,
				states: locationFilters?.states ?? undefined,
				locationSlug: locationFilters?.locationSlug ?? undefined
			};

			emit('update:filters', filtersUpdated);
			return;
		}

		if (property.publicationStatus !== PropertyPublicationStatus.PUBLISHED) {
			return router.push(`/imovel-indisponivel/${property.commercialId}`);
		}

		router.push(`/imovel/${property.commercialId}/${property.slug}`);
	} catch (error) {
		console.error(error);
	} finally {
		isFetchingPropertyByCodeSearch.value = false;
	}
}

function handleSearchSuggestSelectCondo(searchParams: SuggestionCondoResult) {
	emit('filter-applied-for-condo', {
		...currentFilterAppliedParams.value,
		[MixpanelProps.CONDO_NAME]: searchParams.condoName,
		[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
		[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
		[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
		[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
		[MixpanelProps.CURRENT_PAGE]: props.page,
		[MixpanelProps.TEXT_SEARCHED]: searchParams.textSearched,
		[MixpanelProps.FILTERS_APPLIED]: 'condo'
	} as FilterApplied);
	const base = '/condominio/';

	router.push(`${base}${searchParams.condoSlug}`);
}

async function waitForFetchingToComplete() {
	return await until(() => !props.isFetchingProperties).toBeTruthy();
}

async function waitForFetchingToStart() {
	return await until(() => props.isFetchingProperties).toBeTruthy();
}

async function handleEventWithFetchingCheck(
	event: SearchParams,
	filtersUpdated: PropertyFilters
) {
	await waitForFetchingToStart();
	await Promise.all([waitForFetchingToComplete(), waitForTotalResults()]);
	handleEventSuggest(event, filtersUpdated);
}

function handleEmptyResultOnFilter(searchTerm: string) {
	searchPropertyEvents.trackEmptyStateFilter({
		[MixpanelProps.TEXT_SEARCH]: searchTerm
	});
}
function handleEventSuggest(
	event: SelectAddressEvent,
	filtersUpdated: PropertyFilters
) {
	const eventFilters = {
		...filtersUpdated,
		...event
	};

	const changedKeysToFilterSearch = {
		regions: (event?.regions?.length ?? 0) > 0,
		cities: (event?.cities?.length ?? 0) > 0,
		macroregions: (event?.macroregions?.length ?? 0) > 0
	};

	const changedKeysToFilterSearchString = (
		Object.keys(
			changedKeysToFilterSearch
		) as (keyof ChangedKeysToFilterSearch)[]
	).filter(key => changedKeysToFilterSearch[key]);

	const values = getResumedFilterAppliedValues(
		eventFilters,
		'searchBar',
		changedKeysToFilterSearchString,
		event.text
	);

	values[MixpanelProps.TOTAL_RESULTS] = props.countProperties;

	trackFiltersAppliedResumed(values);
}

function addChangedKeyIfValid(
	key: keyof PropertyFilters,
	newValue: AllowedValues | null
) {
	if (Array.isArray(newValue)) {
		const oldValue = filters.value[key] || [];

		if (
			Array.isArray(oldValue) &&
			Array.isArray(newValue) &&
			newValue.length !== oldValue.length
		) {
			changedKeysFilters.value.add(key);
		}
	} else if (newValue) {
		changedKeysFilters.value.add(key);
	}
}

function handleUpdateFilter(
	key: keyof PropertyFilters,
	value: AllowedValues | null
) {
	if (value) {
		addChangedKeyIfValid(key, value);
	}

	emit('update:filters', {
		...filters.value,
		[key]: value || undefined
	});
}

function getComponentNumberOfAppliedFilters(
	componentKeys: (keyof PropertyFilters)[]
): number {
	return props.appliedFilterKeys.filter(key => componentKeys.includes(key))
		.length;
}

const shouldShowCityStatePlaceholder = computed(() => {
	return !!(
		filters.value.regions?.length ||
		filters.value.macroregions?.length ||
		filters.value.cities?.length
	);
});

const placeholder = computed<string | undefined>(() => {
	if (shouldShowCityStatePlaceholder.value) {
		const firstPart =
			filters.value.cities?.[0] || filters.value.macroregions?.[0];
		const secondPart = filters.value.states?.[0] || 'SP';
		if (!firstPart || !secondPart) {
			return undefined;
		}

		return `${firstPart || ''} - ${secondPart || 'SP'}`.replaceAll(',', ' - ');
	}

	return '';
});

const formatedAddressSlug = computed(() => {
	if (filters.value.addressSlug && filters.value.addressSlug.includes(',')) {
		return filters.value.addressSlug;
	}

	return '';
});

const placeholderWithFallback = computed(() => {
	return formatedAddressSlug.value || placeholder.value;
});

const isDialogOpen = ref(false);

searchSuggestBus.on((event: SearchSelectionEvent) => {
	if (event.type === 'address' || event.type === 'place') {
		handleSearchSuggestSelectAddress(event.payload);
	} else if (event.type === 'condo') {
		handleSearchSuggestSelectCondo(event.payload);
	} else if (event.type === 'code') {
		handleSearchSuggestSelectCode(event.payload);
	}
});

const wrapperClasses = computed(() =>
	navVariants({
		transform: filtersBarTransform.value
	})
);

const { headerTransformStyle } = usePropertiesListingHeader();

const filtersBarInlineStyle = computed(() => {
	if (!props.fixed) return undefined;
	return headerTransformStyle.value;
});

const fixedPositionClasses = computed(() => {
	if (!props.fixed) return '';
	// Mobile: fixed position right below the 60px PortalHeader container, z-index 20.
	// When the header hides, filtersBarInlineStyle translates this element up by 60px dynamically with GPU acceleration.
	return 'top-[60px] lg:top-[80px] z-20';
});
</script>

<template>
	<section
		class="flex w-full flex-nowrap items-center justify-start gap-x-2 border-t border-primary-light-4 bg-primary px-4 py-[10px] md:gap-x-3 md:py-3 lg:px-12 lg:py-4"
		:class="[wrapperClasses, fixedPositionClasses]"
		:style="filtersBarInlineStyle"
	>
		<div class="flex min-w-0 flex-1 items-center gap-x-2">
			<div
				class="relative flex h-11 min-w-0 flex-1 max-[960px]:w-full sm:w-full md:h-[50px] min-[960px]:w-[400px] min-[960px]:flex-none"
			>
				<div class="size-full lg:hidden">
					<SearchSuggestDialog
						:regions="filters.regions"
						:macroregions="filters.macroregions"
						:cities="filters.cities"
						:location-slug="filters.locationSlug"
						:current-filters="filters"
						:is-fetching-property-by-code-search="
							isFetchingPropertyByCodeSearch
						"
						:placeholders="placeholder"
						:is-open="isDialogOpen"
						:search-term="''"
						class="w-full border-none shadow-[0_1px_3px_1px_#00000026] md:hidden"
						input-wrapper-classes="bg-primary-light-4 border border-primary-light-2 hover:focus-within:border-white-dark-4 hover:border-primary-light-1 focus-within:border-white-dark-4 pr-3"
						button-input-classes="pr-3 pl-2 h-full text-white bg-primary-light-4 placeholder:text-[#cccccc]/80 text-sm truncate"
						input-classes="min-h-16 text-primary text-base"
						input-clear-icon-wrapper-classes="hidden"
						input-search-icon-wrapper-classes="hidden"
						input-clear-icon-classes="text-primary-light-2"
						empty-state-icon-wrapper-classes="hidden"
						empty-state-title-classes="text-base"
						empty-state-description-classes="text-xs md:text-xs"
						list-wrapper-classes="mt-[11px] md:mt-3 lg:mt-4"
						placeholder-classes="text-[#CCCCCC]/80"
						@select-code="handleSearchSuggestSelectCode"
						@select-address="handleSearchSuggestSelectAddress"
						@select-place="handleSearchSuggestSelectAddress"
						@select-condo="handleSearchSuggestSelectCondo"
						@empty-result-on-filter="handleEmptyResultOnFilter"
						@update:search-term="handleSearchTermUpdate"
					>
						<template #input-prefix>
							<button
								class="border-none bg-transparent pl-3"
								@click="isDialogOpen = true"
							>
								<IconMaterialSymbolsLightSearch class="size-5 text-beige" />
							</button>
						</template>
					</SearchSuggestDialog>
				</div>
				<div class="hidden w-full lg:block">
					<SearchSuggest
						:regions="filters.regions"
						:macroregions="filters.macroregions"
						:cities="filters.cities"
						:location-slug="filters.locationSlug"
						:current-filters="filters"
						:is-fetching-property-by-code-search="
							isFetchingPropertyByCodeSearch
						"
						:search-term="''"
						:placeholders="placeholder"
						class="hidden w-full border-none shadow-[0_1px_3px_1px_#00000026] md:block"
						input-placeholder="Busque por bairros, cidades e regiões"
						input-wrapper-classes="bg-primary-light-4 border border-primary-light-2 hover:focus-within:border-white-dark-4 hover:border-primary-light-1 focus-within:border-white-dark-4"
						input-classes="h-[41px] md:h-[48px] text-white placeholder:text-[#cccccc]/80 focus:placeholder:opacity-100 pr-3 pl-2"
						input-search-icon-wrapper-classes="hidden"
						input-clear-icon-wrapper-classes="pr-3"
						input-clear-icon-classes="text-primary-light-2"
						empty-state-icon-wrapper-classes="hidden"
						empty-state-title-classes="text-base"
						empty-state-description-classes="text-xs md:text-xs"
						list-wrapper-classes="mt-[11px] md:mt-3 lg:mt-4"
						:enable-animation="false"
						@select-code="handleSearchSuggestSelectCode"
						@select-address="handleSearchSuggestSelectAddress"
						@select-place="handleSearchSuggestSelectAddress"
						@select-condo="handleSearchSuggestSelectCondo"
						@empty-result-on-filter="handleEmptyResultOnFilter"
						@update:search-term="handleSearchTermUpdate"
					>
						<template #input-prefix>
							<div class="pl-3">
								<IconMaterialSymbolsLightSearch class="size-5 text-beige" />
							</div>
						</template>
					</SearchSuggest>
				</div>
			</div>
			<div class="hidden min-[960px]:flex">
				<FilterDropdown
					data-testid="regions-filter-dropdown"
					:label="getRegionsLabel()"
					:number-of-applied-filters="
						getComponentNumberOfAppliedFilters(['regions'])
					"
					:show-number-of-applied-filters="false"
					filter-button-variant="dark"
					content-variant="primary"
					class="hidden w-min max-w-[240px] truncate whitespace-nowrap min-[960px]:flex xl:max-w-[256px] 2xl:max-w-[300px]"
					@close="saveClosedFilterStatus"
				>
					<template #label>
						<p
							class="max-w-[204px] truncate whitespace-nowrap xl:max-w-[256px] 2xl:max-w-[300px]"
						>
							{{ getRegionsLabel() }}
						</p>
					</template>
					<template #content>
						<RegionSelect
							variant="primary"
							:regions="filters.regions || []"
							:city="filters.cities?.[0]"
							:state="filters.states?.[0]"
							:count-properties="props.countProperties"
							:is-fetching-properties="props.isFetchingProperties"
							:macroregion="filters.macroregions?.[0]"
							:address-slug="filters.addressSlug"
							@update-selection="updateRegions"
						/>
					</template>
				</FilterDropdown>
			</div>
			<div class="hidden min-[960px]:flex">
				<FilterDropdown
					data-testid="price-filter-dropdown"
					:label="getPriceLabel()"
					:number-of-applied-filters="
						getComponentNumberOfAppliedFilters([
							'minAskingPrice',
							'maxAskingPrice'
						])
					"
					:show-number-of-applied-filters="false"
					filter-button-variant="dark"
					content-variant="primary"
					class="hidden min-[960px]:flex"
					@close="saveClosedFilterStatus"
				>
					<template #content>
						<PropertyPrice
							variant="primary"
							:min="filters.minAskingPrice"
							:max="filters.maxAskingPrice"
							@update:min="handleUpdateFilter('minAskingPrice', $event ?? null)"
							@update:max="handleUpdateFilter('maxAskingPrice', $event ?? null)"
						/>
					</template>
				</FilterDropdown>
			</div>
			<div class="hidden min-[960px]:flex">
				<FilterDropdown
					data-testid="area-filter-dropdown"
					:label="getAreaLabel()"
					:number-of-applied-filters="
						getComponentNumberOfAppliedFilters(['minArea', 'maxArea'])
					"
					:show-number-of-applied-filters="false"
					filter-button-variant="dark"
					content-variant="primary"
					class="hidden min-[960px]:flex"
					@close="saveClosedFilterStatus"
				>
					<template #content>
						<PropertyArea
							variant="primary"
							:min="filters.minArea"
							:max="filters.maxArea"
							@update:min="handleUpdateFilter('minArea', $event ?? null)"
							@update:max="handleUpdateFilter('maxArea', $event ?? null)"
						/>
					</template>
				</FilterDropdown>
			</div>
			<div class="hidden xl:flex">
				<FilterDropdown
					data-testid="property-type-filter-dropdown"
					label="Tipo do imóvel"
					:number-of-applied-filters="
						getComponentNumberOfAppliedFilters(['propertyTypes'])
					"
					:show-number-of-applied-filters="false"
					filter-button-variant="dark"
					content-variant="primary"
					class="hidden xl:flex"
					@close="saveClosedFilterStatus"
				>
					<template #label>
						<div class="flex items-center gap-1">
							<p>Tipo do imóvel</p>

							<div
								v-show="filters.propertyTypes?.length"
								class="size-5 min-w-5 rounded-full bg-primary-light-1"
							>
								{{ filters.propertyTypes?.length }}
							</div>
						</div>
					</template>
					<template #content>
						<PropertyTypeSelect
							variant="primary"
							:model-value="filters.propertyTypes || []"
							@update:model-value="handleUpdateFilter('propertyTypes', $event)"
						/>
					</template>
				</FilterDropdown>
			</div>

			<div class="hidden 2xl:flex">
				<FilterDropdown
					data-testid="rooms-filter-dropdown"
					:number-of-applied-filters="
						getComponentNumberOfAppliedFilters([
							'bedrooms',
							'suites',
							'bathrooms',
							'parkingSpots'
						])
					"
					:show-number-of-applied-filters="false"
					filter-button-variant="dark"
					content-variant="primary"
					class="hidden 2xl:flex"
					@close="saveClosedFilterStatus"
				>
					<template #label>
						<div class="flex items-center gap-1">
							<p>Cômodos</p>

							<div
								v-show="numberOfConvinientFilters"
								class="size-5 min-w-5 rounded-full bg-primary-light-1"
							>
								{{ numberOfConvinientFilters }}
							</div>
						</div>
					</template>
					<template #content>
						<ConvinientSelect
							variant="primary"
							:suites="filters.suites"
							:bathrooms="filters.bathrooms"
							:parking-spots="filters.parkingSpots"
							:bedrooms="filters.bedrooms"
							@update:bedrooms="handleUpdateFilter('bedrooms', $event || null)"
							@update:suites="handleUpdateFilter('suites', $event || null)"
							@update:bathrooms="
								handleUpdateFilter('bathrooms', $event || null)
							"
							@update:parking-spots="
								handleUpdateFilter('parkingSpots', $event || null)
							"
						/>
					</template>
				</FilterDropdown>
			</div>

			<div class="hidden 3xl:flex">
				<FilterDropdown
					data-testid="features-filter-dropdown"
					:number-of-applied-filters="
						getComponentNumberOfAppliedFilters(['features'])
					"
					:show-number-of-applied-filters="false"
					filter-button-variant="dark"
					content-variant="primary"
					class="hidden 3xl:flex"
					@close="saveClosedFilterStatus"
				>
					<template #label>
						<div class="flex items-center gap-1">
							<p>Características</p>

							<div
								v-show="filters.features?.length"
								class="size-5 min-w-5 rounded-full bg-primary-light-1"
							>
								{{ filters.features?.length }}
							</div>
						</div>
					</template>
					<template #content>
						<PropertyFeatureSelect
							variant="primary"
							:model-value="filters.features || []"
							@update:model-value="
								handleUpdateFilter('features', $event || null)
							"
						/>
					</template>
				</FilterDropdown>
			</div>
			<div
				class="ml-auto transition-all duration-300 sm:max-w-none md:ml-[unset]"
				:class="[
					numberOfAppliedClosedFilters === 0 ? 'max-w-[44px]' : 'max-w-[74px]'
				]"
			>
				<FilterButton
					v-model:is-active="isSidebarActive"
					data-testid="sidebar-filter-button"
					variant="dark-reverse"
					:number-of-applied-filters="numberOfAppliedClosedFilters"
				>
					<template #icon>
						<FilterIcon class="order-last size-6 text-beige" />
					</template>
					<template #label>
						<div class="order-1 hidden sm:flex">
							<p class="hidden sm:flex lg:hidden">Filtros</p>
							<p class="hidden lg:flex">Mais filtros</p>
						</div>
					</template>
				</FilterButton>
			</div>
		</div>
	</section>
</template>
